import { useEffect, useRef, useState } from 'react';

import type { Me } from '@inbound/contract';

import { IconLogout } from '../ui/icons.js';
import { initials } from '../ui/initials.js';
import { ThemeSwitch } from './ThemeSwitch.js';
import styles from './UserMenu.module.css';

/**
 * The signed-in user, top right: their picture, and behind it who they are and
 * the way out. The Avatar Assistant's user menu, with the email where that one
 * shows a role — there are no roles here, and the address is the identity the
 * allowlist actually checks.
 *
 * Name and email come from the IdP via /auth/me, not from anything the user
 * typed, so this is the verified identity S03 asks for.
 */
export function UserMenu({
  me,
  onSignOut,
  signingOut,
}: {
  me: Me | undefined;
  onSignOut: () => void;
  signingOut: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // A picture that fails to load (expired link, blocked third-party image) falls
  // back to initials rather than leaving a broken-image icon in the header.
  const [pictureFailed, setPictureFailed] = useState(false);
  const picture = me?.pictureUrl && !pictureFailed ? me.pictureUrl : null;

  const name = me?.name ?? null;
  const email = me?.email ?? '';
  // From the name when there is one, otherwise from the local part of the
  // address — "ivan.petkov@…" reads as IP rather than as I.
  const letters = initials(name ?? email.replace(/@.*$/, '').replace(/[._-]+/g, ' '));

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent): void {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        ref={buttonRef}
        className={styles.avatar}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Account"
        title={name ?? email}
      >
        {picture ? (
          <img
            src={picture}
            alt=""
            // Google and Microsoft both refuse profile pictures requested with
            // a referrer from a site they do not know.
            referrerPolicy="no-referrer"
            onError={() => setPictureFailed(true)}
          />
        ) : (
          letters
        )}
      </button>

      {open && (
        <div className={styles.menu}>
          <div className={styles.head}>
            <b>{name ?? email}</b>
            {name && <span>{email}</span>}
          </div>

          {/* On a phone the header has no room for the theme switch, so it moves
              in here. Hidden above that width, where the header has its own. */}
          <div className={styles.theme}>
            <span>Theme</span>
            <ThemeSwitch />
          </div>

          <button type="button" className={styles.item} onClick={onSignOut} disabled={signingOut}>
            <IconLogout width={16} height={16} />
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  );
}
